import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../utils/api";
import { SlArrowLeft } from "react-icons/sl";
import { Button, Skeleton } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import html2canvas from "html2canvas";

const InvoiceDetail = () => {
  const { id } = useParams();               // id is a string
  const [invoice, setInvoice] = useState(null);

  /* -------------------------------------------------
   *  1. FETCH INVOICE
   * ------------------------------------------------- */
  useEffect(() => {
    api
      .get(`/invoices/${id}`)
      .then((res) => {
        setInvoice(res.data.data);          // <-- note .data.data
      })
      .catch((err) => {
        console.error("Error fetching invoice:", err);
      });
  }, [id]);

  /* -------------------------------------------------
   *  2. CALCULATE TOTAL PAID & TOTAL DUE
   * ------------------------------------------------- */
  const totalPaid = invoice?.payments
    ?.filter((p) => p.status === "Paid")
    .reduce((sum, p) => sum + Number(p.amount), 0) ?? 0;

  const totalDue = Number(invoice?.total_amount ?? 0) - totalPaid;

  /* -------------------------------------------------
   *  3. DOWNLOAD AS PNG
   * ------------------------------------------------- */
  const handleDownloadInvoice = () => {
    const el = document.getElementById("invoice-container");
    if (!el) return;

    html2canvas(el, { scale: 2 }).then((canvas) => {
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `invoice_${invoice?.invoice_number ?? id}.png`;
      link.click();
    });
  };

  /* -------------------------------------------------
   *  4. RENDER
   * ------------------------------------------------- */
  return (
    <div className="w-full bg-[#F5F5F5] min-h-[100vh]">
      {/* Header */}
      <div className="flex font-roboto px-4 py-2 bg-[#3F3F95] rounded-b-lg">
        <div className="items-center my-auto">
          <Link to="/adduserpayment">
            <SlArrowLeft className="text-white" />
          </Link>
        </div>
        <h3 className="font-bold flex justify-center mx-auto text-[22px] text-white">
          Invoice Detail
        </h3>
      </div>

      {invoice ? (
        <>
          {/* ---- INVOICE CONTAINER (captured by html2canvas) ---- */}
          <div
            id="invoice-container"
            className="bg-white p-4 rounded-lg shadow-sm shadow-gray-400 m-4"
          >
            {/* Society Header */}
            <h2 className="text-xl font-bold text-center mb-2">
              Society of Comfort SMD Awas Bayawasthapan Samiti
            </h2>
            <p className="text-center">thaiba-14, Lalitpur</p>

            {/* Bill To */}
            <div className="text-sm font-mono border-b border-gray-400 pb-2 mb-4 mt-8">
              <div className="flex justify-between">
                <p>
                  <strong>House No:</strong> {invoice.house_number}
                </p>
                <p>Owner: {invoice.user_name}</p>
              </div>
            </div>

            {/* Invoice Meta */}
            <div className="text-sm font-mono mb-4 space-y-1">
              <p>
                <strong>Invoice #:</strong> {invoice.invoice_number}
              </p>
              <p>
                <strong>Month:</strong> {invoice.month}
              </p>
              <p>
                <strong>Invoice Date:</strong> {invoice.invoice_date}
              </p>
              <p>
                <strong>Due Date:</strong> {invoice.due_date}
              </p>
            </div>

            {/* Items */}
            <div className="text-sm font-mono border-b border-gray-400 pb-2 mb-4">
              <strong>Items:</strong>
              {invoice.invoice_items && invoice.invoice_items.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {invoice.invoice_items.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col md:flex-row justify-between border-b border-gray-300 py-1"
                    >
                      <span>{item.particular}</span>
                      <span>Qty: {item.quantity}</span>
                      <span>Rate: NPR {item.rate}</span>
                      <span>Total: NPR {item.total}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2">No items</p>
              )}
            </div>

            {/* Totals */}
            <div className="text-sm font-mono space-y-2">
              <p className="flex justify-between">
                <strong>Total Amount</strong>
                <span>NPR {invoice.total_amount}</span>
              </p>

              {totalPaid > 0 && (
                <p className="flex justify-between">
                  <strong>Paid</strong>
                  <span className="text-green-600">NPR {totalPaid}</span>
                </p>
              )}

              <p className="flex justify-between border-t pt-1">
                <strong>Total Amount Due</strong>
                <span
                  className={totalDue > 0 ? "text-red-600" : "text-green-600"}
                >
                  NPR {totalDue.toFixed(2)}
                </span>
              </p>
            </div>

            {/* Payments (optional) */}
            {invoice.payments && invoice.payments.length > 0 && (
              <div className="mt-4 text-sm font-mono">
                <strong>Payments:</strong>
                <div className="mt-2 space-y-1">
                  {invoice.payments.map((p) => (
                    <div
                      key={p.id}
                      className="flex justify-between border-b border-gray-200 py-1"
                    >
                      <span>{p.date}</span>
                      <span>NPR {p.amount}</span>
                      <span
                        className={
                          p.status === "Paid"
                            ? "text-green-600"
                            : "text-orange-600"
                        }
                      >
                        {p.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Remarks – fallback if API ever adds it */}
            <div className="mt-4 text-sm font-mono">
              <strong>Remarks:</strong>
              <p>{invoice.remarks ?? "—"}</p>
            </div>
          </div>

          {/* Download Button */}
          <div className="mx-4 mt-4">
            <Button
              icon={<DownloadOutlined />}
              className="bg-[#19891A] text-white w-full"
              style={{ padding: "20px 0" }}
              onClick={handleDownloadInvoice}
            >
              Download Invoice
            </Button>
          </div>
        </>
      ) : (
        <div className="p-4">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;